import * as React from 'react'

import {
  MAX_SESSION_AGE_MS,
  THRESHOLD_FOR_MANY_REPLIES,
} from '../utils/commentScoreConstants'
import { getReplySessionKeys } from '../utils/commentScoreSessionManager'

export const useCommentReplyTracker = (
  channelIds: string[],
): {
  repliedToManyComments: boolean
  commentsReplied: number
  startSession: () => void
  stopSession: () => void
  incrementReplyCount: () => void
} => {
  const [commentsReplied, setCommentsReplied] = React.useState(0)
  const [repliedToManyComments, setRepliedToManyComments] =
    React.useState(false)

  const { replyCountKey, timestampKey } = getReplySessionKeys(channelIds)

  const resetSession = React.useCallback((): void => {
    localStorage.removeItem(replyCountKey)
    localStorage.removeItem(timestampKey)
    setCommentsReplied(0)
    setRepliedToManyComments(false)
  }, [replyCountKey, timestampKey])

  const startSession = React.useCallback((): void => {
    const existingCount = localStorage.getItem(replyCountKey)
    const existingTimestamp = localStorage.getItem(timestampKey)
    const now = Date.now()

    if (existingCount !== null && existingTimestamp !== null) {
      const timestamp = parseInt(existingTimestamp, 10)
      const isExpired = now - timestamp > MAX_SESSION_AGE_MS
      if (!isExpired) return
      // Clear stale session
      resetSession()
    }

    // Start new session
    localStorage.setItem(replyCountKey, '0')
    localStorage.setItem(timestampKey, now.toString())
    setCommentsReplied(0)
    setRepliedToManyComments(false)
  }, [replyCountKey, timestampKey, resetSession])

  const stopSession = React.useCallback((): void => {
    resetSession()
  }, [resetSession])

  const incrementReplyCount = React.useCallback((): void => {
    const currentCount = parseInt(
      localStorage.getItem(replyCountKey) || '0',
      10,
    )
    const newCount = currentCount + 1

    localStorage.setItem(replyCountKey, newCount.toString())
    setCommentsReplied(newCount)
    setRepliedToManyComments(newCount >= THRESHOLD_FOR_MANY_REPLIES)
  }, [replyCountKey])

  // Rehydrate from localStorage on mount
  React.useEffect(() => {
    const storedCount = localStorage.getItem(replyCountKey)
    const storedTimestamp = localStorage.getItem(timestampKey)

    if (storedCount && storedTimestamp) {
      const timestamp = parseInt(storedTimestamp, 10)
      const now = Date.now()

      const isExpired = now - timestamp > MAX_SESSION_AGE_MS
      if (isExpired) {
        resetSession()
        return
      }

      const count = parseInt(storedCount, 10)
      setCommentsReplied(count)
      setRepliedToManyComments(count >= THRESHOLD_FOR_MANY_REPLIES)
    }
  }, [replyCountKey, timestampKey, resetSession])

  return {
    repliedToManyComments,
    commentsReplied,
    startSession,
    stopSession,
    incrementReplyCount,
  }
}
